import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, GiftIcon, ArrowRightIcon, SparklesIcon, ZapIcon, TrophyIcon, StarIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
interface SpinWheelPopupProps {
  isOpen: boolean;
  onClose: () => void;
  isLoggedIn?: boolean;
}
interface Segment {
  label: string;
  shortLabel: string;
  emoji: string;
  color: string;
  darkColor: string;
}
const SEGMENTS: Segment[] = [{
  label: 'Voucher 1.000đ',
  shortLabel: '1K',
  emoji: '🎫',
  color: '#6B7280',
  darkColor: '#4B5563'
}, {
  label: 'Áo Thun MHP',
  shortLabel: 'Áo',
  emoji: '👕',
  color: '#8B5CF6',
  darkColor: '#7C3AED'
}, {
  label: 'Voucher 10.000đ',
  shortLabel: '10K',
  emoji: '💵',
  color: '#F59E0B',
  darkColor: '#D97706'
}, {
  label: 'Thay Nhớt Free',
  shortLabel: 'Nhớt',
  emoji: '🔧',
  color: '#3B82F6',
  darkColor: '#2563EB'
}, {
  label: 'Voucher 50.000đ',
  shortLabel: '50K',
  emoji: '💰',
  color: '#22C55E',
  darkColor: '#16A34A'
}, {
  label: '500.000đ TÍN DỤNG',
  shortLabel: '500K',
  emoji: '🏆',
  color: '#EF4444',
  darkColor: '#DC2626'
}, {
  label: 'Voucher 5.000đ',
  shortLabel: '5K',
  emoji: '🎟️',
  color: '#EC4899',
  darkColor: '#DB2777'
}, {
  label: 'Nón MHP',
  shortLabel: 'Nón',
  emoji: '🧢',
  color: '#F97316',
  darkColor: '#EA580C'
}];
const WINNING_INDEX = 5; // 500,000đ credit = JACKPOT
export function SpinWheelPopup({
  isOpen,
  onClose,
  isLoggedIn
}: SpinWheelPopupProps) {
  const [phase, setPhase] = useState<'ready' | 'spinning' | 'won'>('ready');
  const [rotation, setRotation] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showFlash, setShowFlash] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wheelSize = 280;
  useEffect(() => {
    if (isOpen) {
      setPhase('ready');
      setRotation(0);
      setShowConfetti(false);
      setShowFlash(false);
    }
  }, [isOpen]);
  // Draw wheel
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = 2;
    canvas.width = wheelSize * dpr;
    canvas.height = wheelSize * dpr;
    ctx.scale(dpr, dpr);
    const center = wheelSize / 2;
    const radius = center - 3;
    const segAngle = 2 * Math.PI / SEGMENTS.length;
    ctx.clearRect(0, 0, wheelSize, wheelSize);
    SEGMENTS.forEach((seg, i) => {
      const startAngle = i * segAngle - Math.PI / 2;
      const endAngle = startAngle + segAngle;
      // Segment gradient
      const midAngle = startAngle + segAngle / 2;
      const gx = center + Math.cos(midAngle) * radius * 0.5;
      const gy = center + Math.sin(midAngle) * radius * 0.5;
      const grad = ctx.createRadialGradient(center, center, radius * 0.1, gx, gy, radius);
      grad.addColorStop(0, seg.color);
      grad.addColorStop(1, seg.darkColor);
      ctx.beginPath();
      ctx.moveTo(center, center);
      ctx.arc(center, center, radius, startAngle, endAngle);
      ctx.closePath();
      ctx.fillStyle = grad;
      ctx.fill();
      // Segment borders
      ctx.strokeStyle = 'rgba(0,0,0,0.4)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      // Inner shine
      ctx.beginPath();
      ctx.moveTo(center, center);
      ctx.arc(center, center, radius, startAngle, startAngle + segAngle * 0.5);
      ctx.closePath();
      ctx.fillStyle = 'rgba(255,255,255,0.06)';
      ctx.fill();
      // Text
      ctx.save();
      ctx.translate(center, center);
      ctx.rotate(startAngle + segAngle / 2);
      // Emoji
      ctx.font = `${i === WINNING_INDEX ? 22 : 18}px system-ui`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(seg.emoji, radius * 0.52, -10);
      // Label
      ctx.fillStyle = '#FFFFFF';
      ctx.shadowColor = 'rgba(0,0,0,0.7)';
      ctx.shadowBlur = 3;
      ctx.font = `bold ${i === WINNING_INDEX ? 11 : 10}px system-ui`;
      ctx.fillText(seg.shortLabel, radius * 0.52, 8);
      // Jackpot star indicator
      if (i === WINNING_INDEX) {
        ctx.font = '8px system-ui';
        ctx.fillStyle = '#FFD700';
        ctx.fillText('JACKPOT', radius * 0.52, 20);
      }
      ctx.shadowBlur = 0;
      ctx.restore();
    });
    // Outer ring
    ctx.beginPath();
    ctx.arc(center, center, radius, 0, 2 * Math.PI);
    ctx.strokeStyle = '#C8A84E';
    ctx.lineWidth = 3;
    ctx.stroke();
    // Inner ring
    ctx.beginPath();
    ctx.arc(center, center, radius - 4, 0, 2 * Math.PI);
    ctx.strokeStyle = 'rgba(200,168,78,0.3)';
    ctx.lineWidth = 1;
    ctx.stroke();
    // Center hub
    const hubGrad = ctx.createRadialGradient(center, center, 0, center, center, 26);
    hubGrad.addColorStop(0, '#FFE066');
    hubGrad.addColorStop(0.4, '#C8A84E');
    hubGrad.addColorStop(1, '#8B7340');
    ctx.beginPath();
    ctx.arc(center, center, 26, 0, 2 * Math.PI);
    ctx.fillStyle = hubGrad;
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.4)';
    ctx.lineWidth = 2;
    ctx.stroke();
    // Hub text
    ctx.fillStyle = '#0D0D0D';
    ctx.font = 'bold 10px system-ui';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('QUAY', center, center - 4);
    ctx.font = 'bold 8px system-ui';
    ctx.fillText('NGAY!', center, center + 7);
  }, []);
  const handleSpin = useCallback(() => {
    if (phase !== 'ready') return;
    setPhase('spinning');
    const segAngle = 360 / SEGMENTS.length;
    const targetCenter = WINNING_INDEX * segAngle + segAngle / 2;
    const targetAngle = 360 - targetCenter;
    // 10-12 full dramatic spins
    const fullSpins = 360 * (10 + Math.random() * 2);
    const finalRotation = fullSpins + targetAngle + (Math.random() * 8 - 4);
    setRotation(finalRotation);
    // Win sequence
    setTimeout(() => {
      setShowFlash(true);
      setTimeout(() => setShowFlash(false), 300);
    }, 6200);
    setTimeout(() => {
      setPhase('won');
      setShowConfetti(true);
    }, 6500);
  }, [phase]);
  // Generate confetti particles
  const confettiParticles = useMemo(() => {
    if (!showConfetti) return [];
    const colors = ['#C8A84E', '#EF4444', '#22C55E', '#3B82F6', '#EC4899', '#F59E0B', '#FFD700', '#8B5CF6'];
    return Array.from({
      length: 80
    }, (_, i) => ({
      id: i,
      x: 40 + Math.random() * 20,
      startY: 30 + Math.random() * 20,
      delay: Math.random() * 0.8,
      duration: 2 + Math.random() * 2,
      color: colors[Math.floor(Math.random() * colors.length)],
      size: 3 + Math.random() * 8,
      shape: Math.random() > 0.5 ? 'square' : 'circle',
      drift: (Math.random() - 0.5) * 200,
      rotateEnd: Math.random() * 720
    }));
  }, [showConfetti]);
  return <AnimatePresence data-id="element-486">
      {isOpen && <div className="fixed inset-0 z-[60] flex items-center justify-center p-3" data-id="element-487">
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-black/85 backdrop-blur-md" onClick={phase === 'spinning' ? undefined : onClose} data-id="element-488" />

          {/* Win flash */}
          <AnimatePresence data-id="element-489">
            {showFlash && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 0.6
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.15
        }} className="absolute inset-0 bg-[#C8A84E] z-[61] pointer-events-none" data-id="element-490" />}
          </AnimatePresence>

          {/* Confetti */}
          <AnimatePresence data-id="element-491">
            {showConfetti && <div className="absolute inset-0 pointer-events-none overflow-hidden z-[63]" data-id="element-492">
                {confettiParticles.map(p => <motion.div key={p.id} initial={{
            x: `${p.x}%`,
            y: `${p.startY}%`,
            opacity: 1,
            rotate: 0,
            scale: 0
          }} animate={{
            y: '120%',
            x: `calc(${p.x}% + ${p.drift}px)`,
            rotate: p.rotateEnd,
            opacity: [0, 1, 1, 1, 0],
            scale: [0, 1.5, 1, 1, 0.5]
          }} transition={{
            duration: p.duration,
            delay: p.delay,
            ease: 'easeOut'
          }} className="absolute" style={{
            width: p.size,
            height: p.size,
            backgroundColor: p.color,
            borderRadius: p.shape === 'circle' ? '50%' : '2px'
          }} data-id="element-493" />)}
              </div>}
          </AnimatePresence>

          {/* Main popup */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.7,
        y: 40
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.7,
        y: 40
      }} transition={{
        type: 'spring',
        stiffness: 350,
        damping: 25
      }} className="relative z-[64] w-full max-w-[400px]" data-id="element-494">
            {/* Close */}
            <button onClick={onClose} className="absolute -top-3 -right-3 z-10 w-9 h-9 bg-[#2A2A2A] border border-[#444] rounded-full flex items-center justify-center text-[#888] hover:text-white hover:bg-[#444] transition-colors shadow-lg" aria-label="Đóng" data-id="element-495">
              <XIcon className="w-4 h-4" data-id="element-496" />
            </button>

            <div className="bg-gradient-to-b from-[#1E1E1E] to-[#111] border-2 border-[#C8A84E]/40 rounded-2xl overflow-hidden shadow-[0_0_60px_rgba(200,168,78,0.15)]" data-id="element-497">
              {/* Header */}
              <div className="relative bg-gradient-to-r from-[#EF4444]/25 via-[#C8A84E]/30 to-[#EF4444]/25 px-5 py-4 text-center border-b border-[#C8A84E]/30" data-id="element-498">
                <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent" animate={{
              x: ['-100%', '100%']
            }} transition={{
              duration: 1.8,
              repeat: Infinity,
              ease: 'linear'
            }} data-id="element-499" />
                <div className="relative" data-id="element-500">
                  <div className="flex items-center justify-center gap-2 mb-1" data-id="element-501">
                    <motion.span animate={{
                  rotate: [0, 20, -20, 0]
                }} transition={{
                  duration: 1.2,
                  repeat: Infinity
                }} className="text-2xl" data-id="element-502">
                      🎰
                    </motion.span>
                    <h2 className="text-xl font-black text-white tracking-tight" data-id="element-503">
                      VÒNG QUAY MAY MẮN
                    </h2>
                    <motion.span animate={{
                  rotate: [0, -20, 20, 0]
                }} transition={{
                  duration: 1.2,
                  repeat: Infinity
                }} className="text-2xl" data-id="element-504">
                      🎁
                    </motion.span>
                  </div>
                  <p className="text-xs text-[#FFD700] font-bold tracking-wide" data-id="element-505">
                    QUAY MIỄN PHÍ — TRÚNG THƯỞNG 100%!
                  </p>
                </div>
              </div>

              {/* Prize list ticker */}
              <div className="bg-[#0D0D0D] py-2 px-4 border-b border-[#2A2A2A] overflow-hidden" data-id="element-506">
                <motion.div className="flex gap-6 whitespace-nowrap" animate={{
              x: ['0%', '-50%']
            }} transition={{
              duration: 12,
              repeat: Infinity,
              ease: 'linear'
            }} data-id="element-507">
                  {[...SEGMENTS, ...SEGMENTS].map((seg, i) => <span key={i} className="text-xs text-[#888] flex items-center gap-1" data-id="element-508">
                      <span data-id="element-509">{seg.emoji}</span>
                      <span data-id="element-510">{seg.label}</span>
                    </span>)}
                </motion.div>
              </div>

              {/* Wheel area */}
              <div className="px-4 pt-5 pb-3" data-id="element-511">
                <div className="relative flex items-center justify-center" style={{
              height: wheelSize + 30
            }} data-id="element-512">
                  {/* Glow behind wheel */}
                  <motion.div className="absolute rounded-full" style={{
                width: wheelSize + 40,
                height: wheelSize + 40,
                background: 'radial-gradient(circle, rgba(200,168,78,0.15) 0%, transparent 70%)'
              }} animate={{
                scale: [1, 1.05, 1],
                opacity: [0.5, 1, 0.5]
              }} transition={{
                duration: 2,
                repeat: Infinity
              }} data-id="element-513" />

                  {/* Pointer at top */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 z-20" data-id="element-514">
                    <motion.div animate={phase === 'spinning' ? {
                  scale: [1, 1.3, 1]
                } : {}} transition={{
                  duration: 0.3,
                  repeat: phase === 'spinning' ? Infinity : 0
                }} data-id="element-515">
                      <div style={{
                    width: 0,
                    height: 0,
                    borderLeft: '14px solid transparent',
                    borderRight: '14px solid transparent',
                    borderTop: '24px solid #C8A84E',
                    filter: 'drop-shadow(0 3px 6px rgba(0,0,0,0.6))'
                  }} data-id="element-516" />
                    </motion.div>
                  </div>

                  {/* Wheel */}
                  <motion.div className="relative z-10" style={{
                width: wheelSize,
                height: wheelSize
              }} animate={{
                rotate: rotation
              }} transition={{
                duration: phase === 'spinning' ? 6 : 0,
                ease: [0.15, 0.85, 0.25, 1]
              }} data-id="element-517">
                    <canvas ref={canvasRef} style={{
                  width: wheelSize,
                  height: wheelSize,
                  borderRadius: '50%',
                  boxShadow: '0 0 0 5px #1A1A1A, 0 0 0 7px #C8A84E, 0 0 0 10px #1A1A1A, 0 10px 40px rgba(0,0,0,0.6)'
                }} data-id="element-518" />
                  </motion.div>

                  {/* LED lights around wheel */}
                  {Array.from({
                length: 20
              }).map((_, i) => {
                const angle = i / 20 * 360;
                const rad = angle * Math.PI / 180;
                const r = wheelSize / 2 + 16;
                return <motion.div key={i} className="absolute rounded-full z-[5]" style={{
                  width: 6,
                  height: 6,
                  left: `calc(50% + ${Math.cos(rad) * r}px - 3px)`,
                  top: `calc(50% + ${Math.sin(rad) * r}px - 3px)`,
                  backgroundColor: i % 3 === 0 ? '#FFD700' : i % 3 === 1 ? '#EF4444' : '#22C55E',
                  boxShadow: `0 0 6px ${i % 3 === 0 ? '#FFD700' : i % 3 === 1 ? '#EF4444' : '#22C55E'}`
                }} animate={{
                  opacity: phase === 'spinning' ? [0.2, 1, 0.2] : [0.4, 1, 0.4],
                  scale: [0.8, 1.3, 0.8]
                }} transition={{
                  duration: phase === 'spinning' ? 0.2 : 1,
                  repeat: Infinity,
                  delay: i * (phase === 'spinning' ? 0.02 : 0.05)
                }} data-id="element-519" />;
              })}
                </div>
              </div>

              {/* Action area */}
              <div className="px-5 pb-5" data-id="element-520">
                <AnimatePresence mode="wait" data-id="element-521">
                  {phase === 'ready' && <motion.div key="ready" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} className="text-center" data-id="element-522">
                      <motion.button onClick={handleSpin} className="w-full py-4 bg-gradient-to-r from-[#EF4444] via-[#FF6B35] to-[#EF4444] rounded-xl text-white font-black text-lg shadow-lg relative overflow-hidden" whileHover={{
                  scale: 1.03
                }} whileTap={{
                  scale: 0.97
                }} animate={{
                  boxShadow: ['0 4px 20px rgba(239,68,68,0.3)', '0 4px 35px rgba(255,107,53,0.5)', '0 4px 20px rgba(239,68,68,0.3)']
                }} transition={{
                  duration: 1.5,
                  repeat: Infinity
                }} data-id="element-523">
                        <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent" animate={{
                    x: ['-100%', '100%']
                  }} transition={{
                    duration: 1,
                    repeat: Infinity
                  }} data-id="element-524" />
                        <span className="relative flex items-center justify-center gap-2" data-id="element-525">
                          <ZapIcon className="w-5 h-5" data-id="element-526" />
                          QUAY NGAY — MIỄN PHÍ!
                          <ZapIcon className="w-5 h-5" data-id="element-527" />
                        </span>
                      </motion.button>
                      <div className="flex items-center justify-center gap-2 mt-2.5" data-id="element-528">
                        <motion.div className="w-1.5 h-1.5 rounded-full bg-[#22C55E]" animate={{
                    opacity: [1, 0.3, 1]
                  }} transition={{
                    duration: 1,
                    repeat: Infinity
                  }} data-id="element-529" />
                        <span className="text-[10px] text-[#22C55E] font-medium" data-id="element-530">
                          Trúng thưởng 100% — Chỉ 1 lần duy nhất!
                        </span>
                      </div>
                    </motion.div>}

                  {phase === 'spinning' && <motion.div key="spinning" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} className="text-center py-3" data-id="element-531">
                      <div className="flex items-center justify-center gap-3" data-id="element-532">
                        <motion.span className="text-2xl" animate={{
                    rotate: 360
                  }} transition={{
                    duration: 0.5,
                    repeat: Infinity,
                    ease: 'linear'
                  }} data-id="element-533">
                          🎰
                        </motion.span>
                        <motion.p className="text-[#FFD700] font-black text-lg tracking-wide" animate={{
                    opacity: [1, 0.4, 1],
                    scale: [1, 1.05, 1]
                  }} transition={{
                    duration: 0.6,
                    repeat: Infinity
                  }} data-id="element-534">
                          ĐANG QUAY...
                        </motion.p>
                        <motion.span className="text-2xl" animate={{
                    rotate: -360
                  }} transition={{
                    duration: 0.5,
                    repeat: Infinity,
                    ease: 'linear'
                  }} data-id="element-535">
                          🎰
                        </motion.span>
                      </div>
                    </motion.div>}

                  {phase === 'won' && <motion.div key="won" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} className="text-center" data-id="element-536">
                      {/* JACKPOT banner */}
                      <motion.div initial={{
                  scale: 0,
                  rotate: -10
                }} animate={{
                  scale: 1,
                  rotate: 0
                }} transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 15,
                  delay: 0.1
                }} className="mb-3" data-id="element-537">
                        <motion.div className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#EF4444] via-[#FFD700] to-[#EF4444] rounded-full shadow-lg" animate={{
                    boxShadow: ['0 0 20px rgba(255,215,0,0.3)', '0 0 40px rgba(255,215,0,0.6)', '0 0 20px rgba(255,215,0,0.3)']
                  }} transition={{
                    duration: 1,
                    repeat: Infinity
                  }} data-id="element-538">
                          <TrophyIcon className="w-5 h-5 text-[#0D0D0D]" data-id="element-539" />
                          <span className="text-[#0D0D0D] font-black text-sm tracking-wider" data-id="element-540">
                            🎉 JACKPOT! 🎉
                          </span>
                          <TrophyIcon className="w-5 h-5 text-[#0D0D0D]" data-id="element-541" />
                        </motion.div>
                      </motion.div>

                      {/* Prize */}
                      <motion.div initial={{
                  opacity: 0,
                  y: 15
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.4
                }} className="mb-1" data-id="element-542">
                        <p className="text-white text-sm font-medium" data-id="element-543">
                          Bạn đã trúng giải thưởng lớn nhất!
                        </p>
                      </motion.div>

                      <motion.div initial={{
                  opacity: 0,
                  scale: 0.3
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 12,
                  delay: 0.6
                }} className="my-3" data-id="element-544">
                        <motion.div animate={{
                    scale: [1, 1.05, 1]
                  }} transition={{
                    duration: 1.5,
                    repeat: Infinity
                  }} data-id="element-545">
                          <span className="text-5xl font-black bg-gradient-to-r from-[#FFD700] via-[#FFF] to-[#FFD700] bg-clip-text text-transparent" data-id="element-546">
                            500.000đ
                          </span>
                        </motion.div>
                        <p className="text-[#C8A84E] font-bold text-base mt-1" data-id="element-547">
                          🏆 TÍN DỤNG MUA HÀNG 🏆
                        </p>
                      </motion.div>

                      <motion.p initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} transition={{
                  delay: 0.9
                }} className="text-xs text-[#B0B0B0] mb-4" data-id="element-548">
                        Tương đương{' '}
                        <span className="text-[#22C55E] font-bold" data-id="element-549">
                          1.000 điểm thưởng
                        </span>{' '}
                        — gấp 5 lần thưởng đăng ký!
                      </motion.p>

                      {/* CTA */}
                      <motion.div initial={{
                  opacity: 0,
                  y: 12
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 1.1
                }} data-id="element-550">
                        <Link to={isLoggedIn ? '/vi' : '/dang-nhap'} onClick={onClose} data-id="element-551">
                          <motion.button className="w-full py-4 bg-gradient-to-r from-[#C8A84E] via-[#FFD700] to-[#C8A84E] rounded-xl text-[#0D0D0D] font-black text-base shadow-xl relative overflow-hidden" whileHover={{
                      scale: 1.02
                    }} whileTap={{
                      scale: 0.98
                    }} animate={{
                      boxShadow: ['0 4px 20px rgba(200,168,78,0.3)', '0 4px 40px rgba(255,215,0,0.5)', '0 4px 20px rgba(200,168,78,0.3)']
                    }} transition={{
                      duration: 1.5,
                      repeat: Infinity
                    }} data-id="element-552">
                            <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent" animate={{
                        x: ['-100%', '100%']
                      }} transition={{
                        duration: 1.5,
                        repeat: Infinity
                      }} data-id="element-553" />
                            <span className="relative flex items-center justify-center gap-2" data-id="element-554">
                              <GiftIcon className="w-5 h-5" data-id="element-555" />
                              {isLoggedIn ? 'NHẬN 500.000đ NGAY!' : 'ĐĂNG KÝ NHẬN 500.000đ!'}
                              <ArrowRightIcon className="w-5 h-5" data-id="element-556" />
                            </span>
                          </motion.button>
                        </Link>

                        <div className="flex items-center justify-center gap-4 mt-3 text-[10px] text-[#888]" data-id="element-557">
                          <span className="flex items-center gap-1" data-id="element-558">
                            <StarIcon className="w-3 h-3 text-[#22C55E]" data-id="element-559" />
                            Miễn phí 100%
                          </span>
                          <span className="flex items-center gap-1" data-id="element-560">
                            <StarIcon className="w-3 h-3 text-[#22C55E]" data-id="element-561" />
                            30 giây đăng ký
                          </span>
                          <span className="flex items-center gap-1" data-id="element-562">
                            <StarIcon className="w-3 h-3 text-[#22C55E]" data-id="element-563" />
                            Nhận ngay
                          </span>
                        </div>
                      </motion.div>

                      {/* Urgency */}
                      <motion.div initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} transition={{
                  delay: 1.5
                }} className="mt-3 flex items-center justify-center gap-1.5" data-id="element-564">
                        <motion.div className="w-2 h-2 rounded-full bg-[#EF4444]" animate={{
                    opacity: [1, 0.2, 1],
                    scale: [1, 1.3, 1]
                  }} transition={{
                    duration: 0.8,
                    repeat: Infinity
                  }} data-id="element-565" />
                        <span className="text-[11px] text-[#EF4444] font-bold" data-id="element-566">
                          ⏰ Phần thưởng hết hạn sau 15 phút!
                        </span>
                      </motion.div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}